import { useState, useEffect } from 'react';
import { useAuth } from '../utils/AuthContext';
import { getDataOnRealtimeDb, saveDataOnRealtimeDb, updateDataOnRealtimeDb, deleteDataOnRealtimeDb } from '../utils/firebase';
import { toast } from 'react-toastify';
import moment from 'moment';

export const useBudget = () => {
    const [budgets, setBudgets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total: 0,
        used: 0,
        remaining: 0,
        overBudget: 0,
        categoryStats: {},
        monthlyStats: {},
        budgetHealth: {},
        billStats: {}
    });
    const [history, setHistory] = useState([]);
    const { currentUser } = useAuth();
    const [categories, setCategories] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchBudgets();
        fetchHistory();
        fetchCategories();
    }, [currentUser]);

    const fetchBudgets = async () => {
        try {
            const [budgetsData, expensesData, billsData] = await Promise.all([
                getDataOnRealtimeDb(`users/${currentUser.uid}/budgets`),
                getDataOnRealtimeDb(`users/${currentUser.uid}/expenses`),
                getDataOnRealtimeDb(`users/${currentUser.uid}/bills`)
            ]);

            if (!budgetsData) {
                setBudgets([]);
                setLoading(false);
                return;
            }

            const budgetsArray = Object.keys(budgetsData).map(key => ({
                id: key,
                ...budgetsData[key]
            }));

            setBudgets(budgetsArray);
            await calculateAndUpdateBudgetStats(budgetsArray, expensesData || {}, billsData || {});
        } catch (error) {
            console.error("Error fetching budgets:", error);
            toast.error("Failed to load budgets");
        } finally {
            setLoading(false);
        }
    };

    const calculateAndUpdateBudgetStats = async (budgetsArray, expensesData, billsData) => {
        const stats = {
            total: 0,
            used: 0,
            remaining: 0,
            overBudget: 0,
            categoryStats: {},
            monthlyStats: {},
            budgetHealth: {},
            billStats: {}
        };

        // Convert expenses and bills objects to arrays
        const expenses = Object.entries(expensesData).map(([id, expense]) => ({
            id,
            ...expense
        }));
        const bills = Object.entries(billsData).map(([id, bill]) => ({
            id,
            ...bill
        }));

        // Calculate total budget and initialize category stats
        budgetsArray.forEach(budget => {
            const amount = Number(budget.amount || 0);
            stats.total += amount;

            if (!stats.categoryStats[budget.category]) {
                stats.categoryStats[budget.category] = {
                    budgeted: 0,
                    spent: 0,
                    billsTotal: 0,
                    paidBillsTotal: 0,
                    totalUsed: 0,
                    remaining: 0,
                    percentageUsed: 0,
                    bills: {
                        total: 0,
                        paid: 0,
                        pending: 0,
                        overdue: 0,
                        count: 0
                    }
                };
            }
            stats.categoryStats[budget.category].budgeted += amount;
        });

        // Process bills first
        bills.forEach(bill => {
            const amount = Number(bill.amount || 0);
            const category = bill.category;
            const month = moment(bill.dueDate).format('YYYY-MM');

            // Initialize bill stats for category if not exists
            if (!stats.billStats[category]) {
                stats.billStats[category] = {
                    total: 0,
                    paid: 0,
                    pending: 0,
                    overdue: 0,
                    count: 0
                };
            }

            // Update bill stats
            stats.billStats[category].total += amount;
            stats.billStats[category].count++;

            if (bill.status === 'paid') {
                stats.billStats[category].paid += amount;
                // Only add paid bills to the total used amount
                if (stats.categoryStats[category]) {
                    stats.categoryStats[category].paidBillsTotal += amount;
                    stats.used += amount;
                }
            } else if (bill.status === 'overdue') {
                stats.billStats[category].overdue += amount;
            } else {
                stats.billStats[category].pending += amount;
            }

            // Update category stats with bill information
            if (stats.categoryStats[category]) {
                stats.categoryStats[category].bills = stats.billStats[category];
                stats.categoryStats[category].billsTotal = stats.billStats[category].total;
            }

            // Update monthly stats for bills
            if (!stats.monthlyStats[month]) {
                stats.monthlyStats[month] = {
                    total: 0,
                    byCategory: {},
                    bills: {}
                };
            }
            if (!stats.monthlyStats[month].bills[category]) {
                stats.monthlyStats[month].bills[category] = {
                    total: 0,
                    paid: 0,
                    pending: 0,
                    overdue: 0
                };
            }
            stats.monthlyStats[month].bills[category].total += amount;
            if (bill.status === 'paid') {
                stats.monthlyStats[month].bills[category].paid += amount;
            } else if (bill.status === 'overdue') {
                stats.monthlyStats[month].bills[category].overdue += amount;
            } else {
                stats.monthlyStats[month].bills[category].pending += amount;
            }
        });

        // Process expenses
        expenses.forEach(expense => {
            const amount = Number(expense.amount || 0);
            const category = expense.category;
            const month = moment(expense.date).format('YYYY-MM');

            // Update category stats
            if (stats.categoryStats[category]) {
                stats.categoryStats[category].spent += amount;
                // Calculate total used (expenses + paid bills) for this category
                stats.categoryStats[category].totalUsed =
                    stats.categoryStats[category].spent + stats.categoryStats[category].paidBillsTotal;
                stats.categoryStats[category].remaining =
                    stats.categoryStats[category].budgeted - stats.categoryStats[category].totalUsed;
                stats.categoryStats[category].percentageUsed =
                    (stats.categoryStats[category].totalUsed / stats.categoryStats[category].budgeted) * 100;
            }

            // Update monthly stats
            if (!stats.monthlyStats[month]) {
                stats.monthlyStats[month] = {
                    total: 0,
                    byCategory: {},
                    bills: {}
                };
            }
            stats.monthlyStats[month].total += amount;
            stats.monthlyStats[month].byCategory[category] =
                (stats.monthlyStats[month].byCategory[category] || 0) + amount;

            // Update total used (already includes paid bills from previous loop)
            stats.used += amount;
        });

        // Calculate remaining and over budget amounts (now includes expenses and paid bills only)
        stats.remaining = Math.max(0, stats.total - stats.used);
        stats.overBudget = stats.used > stats.total ? stats.used - stats.total : 0;

        // Calculate budget health for each category
        Object.keys(stats.categoryStats).forEach(category => {
            const categoryStats = stats.categoryStats[category];
            const totalCommitted = categoryStats.totalUsed; // Now only includes expenses and paid bills
            const percentageCommitted = (totalCommitted / categoryStats.budgeted) * 100;

            stats.budgetHealth[category] = {
                status: percentageCommitted > 100 ? 'over_budget' :
                    percentageCommitted > 80 ? 'warning' : 'healthy',
                message: getBudgetHealthMessage(percentageCommitted),
                totalCommitted,
                percentageCommitted
            };
        });

        setStats(stats);
    };

    const getBudgetHealthMessage = (percentageUsed) => {
        if (percentageUsed > 100) {
            return 'Over budget';
        } else if (percentageUsed > 80) {
            return 'Approaching limit';
        } else if (percentageUsed > 50) {
            return 'Moderate usage';
        } else {
            return 'Healthy';
        }
    };

    const fetchHistory = async () => {
        try {
            const historyData = await getDataOnRealtimeDb(`users/${currentUser.uid}/budgetHistory`);
            if (!historyData) {
                setHistory([]);
                return;
            }
            const historyArray = Object.entries(historyData)
                .map(([key, entry]) => ({
                    id: key,
                    timestamp: entry.timestamp,
                    budgetName: entry.budgetName || 'Unknown Budget',
                    budgetCategory: entry.budgetCategory || '-',
                    action: entry.action,
                    amountChange: entry.amountChange || 0,
                    currentAmount: entry.currentAmount || 0
                }))
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            setHistory(historyArray);
        } catch (error) {
            console.error('Error fetching history:', error);
        }
    };

    const logHistory = async (budgetId, action, previousAmount = null, newAmount = null) => {
        try {
            const budget = budgets.find(b => b.id === budgetId);
            if (!budget && action !== 'deleted') return;

            let amountChange = null;

            switch (action) {
                case 'created':
                    amountChange = 0;
                    break;
                case 'updated':
                    amountChange = newAmount - previousAmount;
                    break;
                case 'expense_added':
                    amountChange = newAmount;
                    break;
                case 'deleted':
                    amountChange = -(budget?.amount || 0);
                    break;
            }

            const historyEntry = {
                timestamp: new Date().toISOString(),
                budgetId,
                budgetName: budget?.name || 'Deleted Budget',
                budgetCategory: budget?.category || '-',
                action,
                amountChange,
                currentAmount: newAmount || 0
            };

            await saveDataOnRealtimeDb(`users/${currentUser.uid}/budgetHistory`, historyEntry);
            await fetchHistory();
        } catch (error) {
            console.error('Error logging history:', error);
        }
    };

    const addBudget = async (values, { resetForm }) => {
        try {
            const { id, ...budgetToAdd } = values;
            budgetToAdd.amount = Math.abs(budgetToAdd.amount);
            budgetToAdd.spent = 0;
            budgetToAdd.status = 'active';

            const budgetId = await saveDataOnRealtimeDb(
                `users/${currentUser.uid}/budgets`,
                budgetToAdd
            );

            await fetchBudgets();
            await logHistory(budgetId, 'created', null, budgetToAdd.amount, 'Budget created');
            resetForm();
            return { success: true, message: "Budget added successfully!" };
        } catch (error) {
            console.error('Error adding budget:', error);
            return { success: false, message: "Failed to add budget" };
        }
    };

    const updateBudget = async (values, { resetForm }) => {
        try {
            const { id, ...budgetToUpdate } = values;
            budgetToUpdate.amount = Math.abs(budgetToUpdate.amount);

            const oldBudget = budgets.find(b => b.id === id);
            await updateDataOnRealtimeDb(`users/${currentUser.uid}/budgets`, id, budgetToUpdate);

            await fetchBudgets();
            await logHistory(
                id,
                'updated',
                oldBudget.amount,
                budgetToUpdate.amount,
                'Budget details updated'
            );
            resetForm();
            return { success: true, message: "Budget updated successfully!" };
        } catch (error) {
            console.error('Error updating budget:', error);
            return { success: false, message: "Failed to update budget" };
        }
    };

    const deleteBudget = async (id) => {
        try {
            await deleteDataOnRealtimeDb(`users/${currentUser.uid}/budgets`, id);
            await fetchBudgets();
            await logHistory(id, 'deleted', null, null, 'Budget deleted');
            return { success: true, message: "Budget deleted successfully!" };
        } catch (error) {
            console.error('Error deleting budget:', error);
            return { success: false, message: "Failed to delete budget" };
        }
    };

    const addExpense = async (budgetId, amount, description = '') => {
        try {
            const budget = budgets.find(b => b.id === budgetId);
            if (!budget) return { success: false, message: "Budget not found" };

            const newSpent = parseFloat(budget.spent || 0) + parseFloat(amount);
            const updateData = {
                ...budget,
                spent: newSpent,
                status: newSpent > budget.amount ? 'over_budget' : 'active'
            };

            await updateDataOnRealtimeDb(
                `users/${currentUser.uid}/budgets`,
                budgetId,
                updateData
            );

            await logHistory(
                budgetId,
                'expense_added',
                budget.spent,
                amount,
                description
            );

            // Recalculate budget stats
            await fetchBudgets();

            return { success: true, message: `Expense added successfully!` };
        } catch (error) {
            console.error('Error adding expense:', error);
            return { success: false, message: "Failed to add expense" };
        }
    };

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const categoriesData = await getDataOnRealtimeDb(`users/${currentUser.uid}/category/budget`);

            if (categoriesData) {
                // If there's a nested structure (like with an ID), get the first object's values
                const firstKey = Object.keys(categoriesData)[0];
                const categoryList = firstKey && typeof categoriesData[firstKey] === 'object'
                    ? Object.values(categoriesData[firstKey])
                    : Object.values(categoriesData);

                setCategories(categoryList);
            } else {
                setCategories([]);
            }
            setError(null);
        } catch (err) {
            console.error('Error fetching budget categories:', err);
            setError('Failed to load budget categories');
            setCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const clearHistory = async () => {
        try {
            await deleteDataOnRealtimeDb(`users/${currentUser.uid}`, 'budgetHistory');
            setHistory([]);
            toast.success('Budget history cleared successfully');
            return { success: true, message: "History cleared successfully!" };
        } catch (error) {
            console.error('Error clearing history:', error);
            toast.error('Failed to clear budget history');
            return { success: false, message: "Failed to clear history" };
        }
    };


    const calculateSpent = () => {
        // Get spent amount for each budget from stats
        const budgetsWithSpent = budgets.map(budget => ({
            ...budget,
            spent: stats.categoryStats[budget.category]?.spent || 0 + stats.billStats[budget.category]?.paid || 0
        }));

        return budgetsWithSpent;
    }

    return {
        budgets,
        loading,
        stats,
        history,
        categories,
        error,
        addBudget,
        updateBudget,
        deleteBudget,
        clearHistory,
        addExpense,
        calculateSpent
    };
}; 